// Довідник дорожніх знаків (вкладка /signs).
//   - Список груп зверху як таби
//   - Сітка знаків (картинка + код + назва)
//   - Тап на знак → модалка з повним описом
//
// Дані: GET /api/v1/signs (групи + знаки)  →  GET /api/v1/signs/:code (деталі)
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError } from '../lib/api';
import { useFetch } from '../lib/useFetch';
import { PageHeader } from '../components/PageHeader';
import { Loader } from '../components/Loader';
import { ErrorView } from '../components/ErrorView';
import { haptic } from '../lib/telegram';
import { IconTile, LockBadge } from '../components/AppIcons';

export function Signs() {
  const { data, loading, error, reload } = useFetch(() => api.signs(), []);
  const [activeGroup, setActiveGroup] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [openCode, setOpenCode] = useState<string | null>(null);

  // При першому завантаженні — обираємо першу групу як активну
  const defaultGroup = data?.groups?.[0]?.code ?? null;
  const selectedGroup = activeGroup ?? defaultGroup;

  const filteredGroup = useMemo(() => {
    if (!data) return null;
    const g = data.groups.find((x) => x.code === selectedGroup);
    if (!g) return null;
    if (!query.trim()) return g;
    const q = query.trim().toLowerCase();
    return {
      ...g,
      signs: g.signs.filter(
        (s) => s.code.includes(q) || s.nameUk.toLowerCase().includes(q),
      ),
    };
  }, [data, selectedGroup, query]);

  if (loading) return <Loader />;
  // 403 = нема Premium — показуємо пейвол замість помилки
  if (error instanceof ApiError && error.status === 403) {
    return (
      <>
        <PageHeader title="Дорожні знаки" />
        <div className="p-4">
          <div className="card flex flex-col items-center gap-3 py-8 text-center">
            <div className="relative">
              <IconTile kind="signs" className="h-16 w-16" glyphClass="h-9 w-9" dimmed />
              <LockBadge className="absolute -bottom-1 -right-1" />
            </div>
            <div className="text-lg font-semibold">Довідник знаків — у Premium</div>
            <p className="max-w-xs text-sm text-tg-hint">
              370+ дорожніх знаків з офіційними описами: попереджувальні, пріоритету,
              заборонні та всі інші — завжди під рукою.
            </p>
            <Link to="/premium" onClick={() => haptic.light()} className="btn-primary mt-1 px-6">
              Активувати Premium
            </Link>
          </div>
        </div>
      </>
    );
  }
  if (error) return <ErrorView error={error} onRetry={reload} />;
  if (!data) return null;

  return (
    <>
      <PageHeader title="Дорожні знаки" />

      <div className="space-y-3 p-4">
        {/* Пошук */}
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Пошук за номером або назвою (наприклад 5.51 або «пам'ятка»)"
          className="w-full rounded-lg bg-tg-secondary-bg px-3 py-2.5 text-sm placeholder:text-tg-hint"
        />

        {/* Табі по групах */}
        <div className="-mx-4 overflow-x-auto px-4">
          <div className="flex gap-1.5 pb-1">
            {data.groups.map((g) => (
              <button
                key={g.code}
                onClick={() => {
                  setActiveGroup(g.code);
                  haptic.light();
                }}
                className={`whitespace-nowrap rounded-lg px-3 py-1.5 text-xs font-medium ${
                  g.code === selectedGroup
                    ? 'bg-tg-button text-tg-button-text'
                    : 'bg-tg-secondary-bg text-tg-text'
                }`}
              >
                {g.code}. {g.name}{' '}
                <span className="opacity-70">({g.signs.length})</span>
              </button>
            ))}
          </div>
        </div>

        {/* Сітка знаків */}
        {filteredGroup && filteredGroup.signs.length > 0 ? (
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
            {filteredGroup.signs.map((s) => (
              <button
                key={s.id}
                onClick={() => {
                  setOpenCode(s.code);
                  haptic.light();
                }}
                className="flex flex-col items-center rounded-lg bg-tg-secondary-bg p-3 text-center transition-transform active:scale-95"
              >
                <div className="flex h-24 w-24 items-center justify-center">
                  <img
                    src={s.imageUrl}
                    alt={s.nameUk}
                    loading="lazy"
                    className="max-h-full max-w-full object-contain"
                  />
                </div>
                <div className="mt-2 text-[11px] font-semibold text-tg-button">
                  {s.code}
                </div>
                <div className="mt-0.5 line-clamp-2 text-[11px] leading-tight text-tg-text">
                  {s.nameUk}
                </div>
              </button>
            ))}
          </div>
        ) : (
          <div className="rounded-lg bg-tg-secondary-bg p-6 text-center text-sm text-tg-hint">
            Нічого не знайдено за запитом «{query}»
          </div>
        )}

        <p className="pt-2 text-center text-[11px] text-tg-hint">
          Усього у довіднику: {data.groups.reduce((a, g) => a + g.signs.length, 0)} знаків.
          <br />
          Дані оновлюються разом із редакцією ПДР України.
        </p>
      </div>

      {openCode && <SignModal code={openCode} onClose={() => setOpenCode(null)} />}
    </>
  );
}

// ============================================================================
// Модалка з повним описом знаку
// ============================================================================
function SignModal({ code, onClose }: { code: string; onClose: () => void }) {
  const { data, loading, error } = useFetch(() => api.signDetail(code), [code]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 p-0 sm:items-center sm:p-4"
      onClick={onClose}
    >
      <div
        className="max-h-[90vh] w-full max-w-md overflow-y-auto rounded-t-2xl bg-tg-bg p-4 sm:rounded-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {loading && <Loader />}
        {error && (
          <p className="text-sm text-tg-destructive">
            {error instanceof ApiError ? error.message : 'Помилка завантаження'}
          </p>
        )}
        {data && (
          <>
            <div className="flex items-start gap-3">
              <img
                src={data.imageUrl}
                alt={data.nameUk}
                className="h-32 w-32 flex-shrink-0 object-contain"
              />
              <div className="flex-1 min-w-0">
                <div className="text-xs font-semibold text-tg-button">{data.groupName}</div>
                <h2 className="mt-1 text-lg font-bold">
                  {data.code}. {data.nameUk}
                </h2>
              </div>
            </div>

            <div className="mt-4 rounded-lg bg-tg-secondary-bg p-3">
              <p className="text-sm leading-relaxed whitespace-pre-line">
                {data.descriptionUk || 'Опис не вказано.'}
              </p>
            </div>

            <button
              onClick={onClose}
              className="btn-primary mt-4 w-full"
            >
              Закрити
            </button>
          </>
        )}
      </div>
    </div>
  );
}
